import { Either, fold } from "fp-ts/lib/Either";
import * as t from "io-ts";
import { makeWordPressAjaxRequest } from "@/lib/wordpress/ajax";
import { _Error } from "@/lib/wordpress/data";
import { ChatMessage } from "./types";
import { fpJsonStringify } from "@/util/fp-json-stringify";
import { pipe } from "fp-ts/lib/function";
import fpJsonParseTypesafe from "@/util/fp-json-parse-typesafe";
import { ioTsToJsonSchema } from "./util";
import { callRequestStructuredOutputFromRailway } from "@/futurelayer/api/api";

export async function requestStructuredOutput<A>(
  message: string,
  GuardV: t.Type<A, any, unknown>,
  abortController: AbortController,
  useRailwayServer: boolean,
): Promise<A | undefined> {

  if (useRailwayServer) {
    return newRequestStructuredOutput(message, GuardV, abortController);
  }

  const ValidGuardV = t.type({
    response: GuardV
  });

  console.log(message);

  const ajaxRequestResponse = await _requestStructuredOutput(
    [
      {
        role: "user",
        content: message
      }
    ],
    {
      name: "my_schema",
      strict: true,
      schema: ioTsToJsonSchema(ValidGuardV)
    },
    abortController
  );

  const validated = pipe(
    ajaxRequestResponse,
    fold(
      () => {
        // AJAX returned invalid response
        return undefined;
      },
      successfulResponse => {
        console.log("AJAX returned a valid response", successfulResponse);

        const parsedResponse = fpJsonParseTypesafe(
          successfulResponse,
          ValidGuardV
        );

        return pipe(
          parsedResponse,
          fold(
            () => {
              // iots errors
              return undefined;
            },
            validObject => {
              return validObject.response;
            }
          )
        );
      }
    )
  );

  return validated;
}

async function _requestStructuredOutput(
  messages: ChatMessage[],
  json_schema: {},
  abortController: AbortController
): Promise<Either<_Error[], string>> {
  
  return makeWordPressAjaxRequest({
    action: "breakdance_ai_request_structured_output",
    friendlyName: "AI Assistant",
    dataToPost: {
      messages: fpJsonStringify(messages),
      json_schema: fpJsonStringify(json_schema)
    },
    abortController,
    decoder: t.string,
    resultKey: "data"
  });
}

export async function newRequestStructuredOutput<A>(
  message: string,
  GuardV: t.Type<A, any, unknown>,
  abortController: AbortController
): Promise<A | undefined> {
  const ValidGuardV = t.type({
    response: GuardV
  });

  console.log(message);

  try {
    const railwayResponse = await callRequestStructuredOutputFromRailway(
      [
        {
          role: "user",
          content: message
        }
      ],
      {
        name: "my_schema",
        strict: true,
        schema: ioTsToJsonSchema(ValidGuardV)
      },
      abortController
    );

    if (!railwayResponse || !railwayResponse.content) {
      return undefined;
    }

    console.log("Railway returned a valid response", railwayResponse.content);

    const parsedResponse = fpJsonParseTypesafe(
      railwayResponse.content,
      ValidGuardV
    );

    return pipe(
      parsedResponse,
      fold(
        () => {
          console.error("Failed to parse Railway response");
          return undefined;
        },
        validObject => {
          return validObject.response;
        }
      )
    );
  } catch (error) {
    console.error("Railway structured output request failed:", error);
    return undefined;
  }
}
